import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import type { Chart as ChartInstance } from "chart.js";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (options: { client_id: string; callback: (r: { credential?: string }) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

type RegistrationRow = {
  id: number;
  created_at: string;
  form_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  gender: string | null;
  birthday: string | null;
  age_confirm: boolean | null;
  terms_accepted: boolean | null;
  marketing_opt_in: boolean | null;
};

type LoveTypeRow = {
  submission_id: string | null;
  created_at: string;
  email: string;
  name: string | null;
  phone?: string | null;
  gender: string | null;
  age_range: string | null;
  l_f: number | null;
  e_r: number | null;
  a_n: number | null;
  g_c: number | null;
  lovetype_code: string | null;
};

type StatsDay = {
  day: string;
  registrations: number;
  lovetype: number;
  eventRegistrations: number;
};

type StatsTotals = {
  registrations: number;
  lovetype: number;
  eventRegistrations: number;
};

type PendingDelete = { kind: "lovetype"; submissionId: string; label: string };

const tokenStorageKey = "aurays_admin_token";

export default function AuraysAdminPage() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [activeTab, setActiveTab] = useState<"registrations" | "lovetype">("lovetype");
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [registrations, setRegistrations] = useState<RegistrationRow[]>([]);
  const [registrationsTotal, setRegistrationsTotal] = useState<number | null>(null);

  const [loveTypes, setLoveTypes] = useState<LoveTypeRow[]>([]);
  const [loveTypesTotal, setLoveTypesTotal] = useState<number | null>(null);

  const [statsDays, setStatsDays] = useState<StatsDay[]>([]);
  const [statsTotals, setStatsTotals] = useState<StatsTotals | null>(null);
  const [statsRangeDays, setStatsRangeDays] = useState(30);
  const chartCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartInstance | null>(null);

  const authHeaders = useMemo(() => {
    const t = token.trim();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  const setAdminToken = (nextToken: string) => {
    const t = nextToken.trim();
    if (t) {
      sessionStorage.setItem(tokenStorageKey, t);
      setToken(t);
    }
  };

  useEffect(() => {
    const savedToken = sessionStorage.getItem(tokenStorageKey);
    if (savedToken) {
      setToken(savedToken);
      setAuthed(true);
    }
  }, []);

  const loginWithGoogleIdToken = useCallback(async (idToken: string) => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/login/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        token?: string | null;
        error?: string;
      } | null;
      if (!res.ok || !data?.ok || typeof data.token !== "string" || !data.token.trim()) {
        setAuthed(false);
        setError(data?.error || "Google login failed.");
        return;
      }
      setAdminToken(data.token);
      setAuthed(true);
    } catch {
      setAuthed(false);
      setError("Google login failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) return;
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
    const container = googleButtonRef.current;
    if (!clientId || !container) return;

    const render = () => {
      const google = window.google?.accounts?.id;
      if (!google?.initialize || !google?.renderButton) return;
      container.innerHTML = "";
      google.initialize({
        client_id: clientId,
        callback: (response) => {
          const credential = typeof response?.credential === "string" ? response.credential : "";
          if (credential) void loginWithGoogleIdToken(credential);
        },
      });
      google.renderButton(container, {
        type: "standard",
        theme: "outline",
        size: "large",
        width: 320,
        text: "signin_with",
        shape: "pill",
      });
    };

    if (window.google?.accounts?.id) {
      render();
      return;
    }

    const existing = document.getElementById("google-gsi");
    if (existing) {
      const interval = window.setInterval(() => {
        if (window.google?.accounts?.id) {
          window.clearInterval(interval);
          render();
        }
      }, 50);
      return () => window.clearInterval(interval);
    }

    const script = document.createElement("script");
    script.id = "google-gsi";
    script.async = true;
    script.defer = true;
    script.src = "https://accounts.google.com/gsi/client";
    script.onload = () => render();
    document.head.appendChild(script);
  }, [authed, loginWithGoogleIdToken]);

  const loadStats = useCallback(async () => {
    setError("");
    try {
      const res = await fetch(`/api/admin/stats?days=${encodeURIComponent(String(statsRangeDays))}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as {
        ok?: boolean;
        days?: StatsDay[];
        totals?: StatsTotals | null;
        bucketedByMonth?: boolean;
      };
      if (!data.ok) throw new Error("Request failed");
      setStatsDays(Array.isArray(data.days) ? data.days : []);
      setStatsTotals(data.totals ?? null);
    } catch {
      setError("Failed to load stats.");
      setStatsDays([]);
      setStatsTotals(null);
    }
  }, [authHeaders, statsRangeDays]);

  const loadRegistrations = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/registrations?limit=100&offset=0", { headers: authHeaders });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as { ok: boolean; total: number; rows: RegistrationRow[] };
      if (!data.ok) throw new Error("Request failed");
      setRegistrationsTotal(data.total ?? 0);
      setRegistrations(data.rows ?? []);
    } catch {
      setError("Failed to load registrations.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const loadLoveTypes = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/lovetype?limit=100&offset=0", { headers: authHeaders });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as { ok: boolean; total: number; rows: LoveTypeRow[] };
      if (!data.ok) throw new Error("Request failed");
      setLoveTypesTotal(data.total ?? 0);
      setLoveTypes(data.rows ?? []);
    } catch {
      setError("Failed to load LoveType submissions.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const deleteRecord = async () => {
    if (!pendingDelete) return;
    setError("");
    setDeleteLoading(true);
    try {
      const url = `/api/admin/lovetype/${encodeURIComponent(pendingDelete.submissionId)}`;
      const res = await fetch(url, { method: "DELETE", headers: authHeaders });
      const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
      if (!res.ok || !data?.ok) throw new Error("delete_failed");
      setPendingDelete(null);
      await loadStats();
      await loadLoveTypes();
    } catch {
      setError("Failed to delete record.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const refreshCurrentTab = useCallback(async () => {
    if (activeTab === "registrations") {
      await loadRegistrations();
      return;
    }
    await loadLoveTypes();
  }, [activeTab, loadRegistrations, loadLoveTypes]);

  useEffect(() => {
    if (!authed || !token.trim()) return;
    void loadStats();
    if (activeTab === "registrations") void loadRegistrations();
    if (activeTab === "lovetype") void loadLoveTypes();
  }, [authed, token, activeTab, loadRegistrations, loadLoveTypes, loadStats]);

  useEffect(() => {
    const canvas = chartCanvasRef.current;
    if (!canvas) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (statsDays.length === 0) return;

    chartRef.current = new Chart(canvas, {
      type: "line",
      data: {
        labels: statsDays.map((day) => day.day),
        datasets: [
          {
            label: "Registrations",
            data: statsDays.map((day) => Number(day.registrations) || 0),
            borderColor: "#9B72FF",
            backgroundColor: "rgba(155, 114, 255, 0.12)",
            fill: true,
            tension: 0.35,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderWidth: 2.5,
          },
          {
            label: "LoveType",
            data: statsDays.map((day) => Number(day.lovetype) || 0),
            borderColor: "#111827",
            backgroundColor: "rgba(17, 24, 39, 0.06)",
            fill: true,
            tension: 0.35,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderWidth: 2.5,
          },
          {
            label: "Event Registrations",
            data: statsDays.map((day) => Number(day.eventRegistrations) || 0),
            borderColor: "#10B981",
            backgroundColor: "rgba(16, 185, 129, 0.12)",
            fill: true,
            tension: 0.35,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderWidth: 2.5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: true, position: "top", labels: { boxWidth: 12, padding: 16, font: { size: 11 } } },
          tooltip: { enabled: true },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxTicksLimit: 6, color: "#6B7280", font: { size: 11 } },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#E5E7EB" },
            ticks: { precision: 0, color: "#6B7280", font: { size: 11 } },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [statsDays]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />
      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">Waitlist registrations &amp; LoveType quiz submissions.</p>
              </div>
            </div>

            {!authed ? (
              <div className="max-w-md">
                {import.meta.env.VITE_GOOGLE_CLIENT_ID ? (
                  <div className="mb-3">
                    <div ref={googleButtonRef} className="min-h-[44px]" />
                  </div>
                ) : (
                  <p className="text-sm text-red-600">Missing VITE_GOOGLE_CLIENT_ID. Google login is not configured.</p>
                )}
                {error ? <p className="text-sm text-red-600 mt-3">{error}</p> : null}
              </div>
            ) : (
              <>
                <div className="border border-gray-100 rounded-2xl p-4 md:p-5 mb-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Activity</p>
                      <p className="text-xs text-gray-500">
                        Registrations, LoveType &amp; Event Registrations over time
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={statsRangeDays}
                        onChange={(event) => setStatsRangeDays(Number(event.target.value))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 bg-white"
                      >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={365}>Last 12 months</option>
                        <option value={0}>All Time</option>
                      </select>
                      <button
                        onClick={() => void loadStats()}
                        className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        Refresh
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                    <div className="rounded-2xl border border-gray-100 bg-[#F8F5FF] p-4">
                      <p className="text-xs text-[#7C55CC] mb-1">Waitlist Registrations</p>
                      <p className="text-2xl font-bold text-[#5B3FA0]">
                        {statsTotals?.registrations?.toLocaleString() ?? "—"}
                      </p>
                      <p className="text-[11px] text-[#9B72FF]/80 mt-1">Total in selected period</p>
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                      <p className="text-xs text-gray-500 mb-1">LoveType Quiz Submissions</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {statsTotals?.lovetype?.toLocaleString() ?? "—"}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-1">Total in selected period</p>
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-emerald-50 p-4">
                      <p className="text-xs text-emerald-700 mb-1">Event Registrations / 活動報名人數</p>
                      <p className="text-2xl font-bold text-emerald-700">
                        {statsTotals?.eventRegistrations?.toLocaleString() ?? "—"}
                      </p>
                      <p className="text-[11px] text-emerald-600/80 mt-1">Total users signed up for any event</p>
                    </div>
                  </div>

                  <div className="w-full overflow-x-auto">
                    <div className="min-w-[680px]">
                      <div className="h-[240px]">
                        <canvas ref={chartCanvasRef} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setActiveTab("lovetype")}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                        activeTab === "lovetype"
                          ? "bg-[#E6D9FF] text-[#5B3FA0]"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      LoveType ({loveTypesTotal ?? "—"})
                    </button>
                    <button
                      onClick={() => setActiveTab("registrations")}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                        activeTab === "registrations"
                          ? "bg-[#E6D9FF] text-[#5B3FA0]"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Registrations ({registrationsTotal ?? "—"})
                    </button>
                  </div>

                  <button
                    onClick={() => void refreshCurrentTab()}
                    disabled={loading}
                    className={`px-4 py-2 rounded-full text-sm font-semibold text-white transition-colors whitespace-nowrap ${
                      loading ? "bg-gray-300 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-700"
                    }`}
                  >
                    {loading ? "Loading..." : "Refresh"}
                  </button>
                </div>

                {error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null}

                {activeTab === "lovetype" ? (
                  <div className="overflow-auto border border-gray-100 rounded-2xl">
                    <table className="min-w-[980px] w-full text-left text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Created</th>
                          <th className="px-4 py-3 font-semibold">Email</th>
                          <th className="px-4 py-3 font-semibold">Name</th>
                          <th className="px-4 py-3 font-semibold">Phone</th>
                          <th className="px-4 py-3 font-semibold">Gender</th>
                          <th className="px-4 py-3 font-semibold">Age</th>
                          <th className="px-4 py-3 font-semibold">Scores</th>
                          <th className="px-4 py-3 font-semibold">Code</th>
                          <th className="px-4 py-3 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {loveTypes.map((row) => (
                          <tr key={row.submission_id ?? row.created_at} className="text-gray-800">
                            <td className="px-4 py-3 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                            <td className="px-4 py-3">{row.email}</td>
                            <td className="px-4 py-3">{row.name || "-"}</td>
                            <td className="px-4 py-3">{row.phone || "-"}</td>
                            <td className="px-4 py-3">{row.gender || "-"}</td>
                            <td className="px-4 py-3">{row.age_range || "-"}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              l_f:{row.l_f ?? "-"} e_r:{row.e_r ?? "-"} a_n:{row.a_n ?? "-"} g_c:{row.g_c ?? "-"}
                            </td>
                            <td className="px-4 py-3 font-semibold">{row.lovetype_code || "-"}</td>
                            <td className="px-4 py-3">
                              {row.submission_id ? (
                                <button
                                  onClick={() =>
                                    setPendingDelete({
                                      kind: "lovetype",
                                      submissionId: row.submission_id!,
                                      label: `${row.email} · ${new Date(row.created_at).toLocaleString()}`,
                                    })
                                  }
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                >
                                  Delete
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))}
                        {loveTypes.length === 0 && !loading ? (
                          <tr>
                            <td className="px-4 py-6 text-gray-500" colSpan={9}>
                              No LoveType submissions.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="overflow-auto border border-gray-100 rounded-2xl">
                    <table className="min-w-[900px] w-full text-left text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Created</th>
                          <th className="px-4 py-3 font-semibold">Name</th>
                          <th className="px-4 py-3 font-semibold">Email</th>
                          <th className="px-4 py-3 font-semibold">Phone</th>
                          <th className="px-4 py-3 font-semibold">Gender</th>
                          <th className="px-4 py-3 font-semibold">Birthday</th>
                          <th className="px-4 py-3 font-semibold">Marketing</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {registrations.map((row) => (
                          <tr key={row.id} className="text-gray-800">
                            <td className="px-4 py-3 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                            <td className="px-4 py-3">{row.name}</td>
                            <td className="px-4 py-3">{row.email}</td>
                            <td className="px-4 py-3">{row.phone || "-"}</td>
                            <td className="px-4 py-3">{row.gender || "-"}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{row.birthday || "-"}</td>
                            <td className="px-4 py-3">
                              {row.marketing_opt_in === null ? "-" : row.marketing_opt_in ? "Yes" : "No"}
                            </td>
                          </tr>
                        ))}
                        {registrations.length === 0 && !loading ? (
                          <tr>
                            <td className="px-4 py-6 text-gray-500" colSpan={7}>
                              No registrations.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {pendingDelete ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => (deleteLoading ? null : setPendingDelete(null))}
          />
          <div className="relative w-full max-w-md bg-white rounded-3xl border border-gray-100 shadow-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm delete</h3>
            <p className="text-sm text-gray-600 mb-5">
              Delete this LoveType submission?
              <br />
              <span className="text-gray-900 font-semibold">{pendingDelete.label}</span>
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleteLoading}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  deleteLoading
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => void deleteRecord()}
                disabled={deleteLoading}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors ${
                  deleteLoading ? "bg-red-300 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
